import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ActivationFormStep from './components/ActivationFormStep';
import ActivationSuccessStep from './components/ActivationSuccessStep';
import CodeEntryStep from './components/CodeEntryStep';

// ─── Shared types ─────────────────────────────────────────────────────────────

export type PackageSummary = {
  giftCardId: string;
  planId: string;
  planName: string;
  programId: string;
  programName: string;
  sessionCount: number;
  /** Sale/discounted price — used as fallback when originalPrice is unavailable */
  amount: number;
  /** Full face-value price. Always shown on card visuals; falls back to amount if absent */
  originalPrice?: number;
  currency: string;
};

export type AccountPreview = {
  userId: string;
  displayName: string;
  profileImage: string | null;
  phoneMasked: string | null;
};

export type ActivationState = {
  /** 0 = enter code, 1 = activation form, 2 = success */
  step: 0 | 1 | 2;
  code: string;
  packageSummary: PackageSummary | null;
  activationSessionToken: string;
  accountPreview: AccountPreview | null;
  completionStatus: string;
  flow: 'ACTIVATE_FOR_SELF' | 'PAY_AND_GIFT' | null;
};

const INITIAL_STATE: ActivationState = {
  step: 0,
  code: '',
  packageSummary: null,
  activationSessionToken: '',
  accountPreview: null,
  completionStatus: '',
  flow: null,
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function GiftCardActivation() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<ActivationState>(INITIAL_STATE);

  const initialCode = searchParams.get('code') ?? '';
  const defaultTab = (searchParams.get('tab') === 'self' ? 'self' : 'gift') as 'gift' | 'self';

  useEffect(() => {
    document.title = 'Activate Gift Card | FitStatic';
  }, []);

  // Always start at the top of the page when advancing or going back
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [state.step]);

  const advance = (partial: Partial<ActivationState>) => {
    setState((prev) => ({
      ...prev,
      ...partial,
      step: Math.min(prev.step + 1, 2) as ActivationState['step'],
    }));
  };

  const back = () => {
    setState((prev) => ({
      ...prev,
      step: Math.max(prev.step - 1, 0) as ActivationState['step'],
    }));
  };

  const reset = () => setState(INITIAL_STATE);

  // Step 1 uses a wider container to accommodate the 2-column layout.
  // Steps 0 and 2 stay narrow (single-column forms / confirmation).
  const containerMaxW =
    state.step === 1 ? 'max-w-[1100px]' :
    state.step === 2 ? 'max-w-[580px]'  :
                       'max-w-[520px]';

  return (
    <div className='min-h-screen bg-bgPrimary'>
      <div className={`${containerMaxW} mx-auto px-4 py-10 transition-all duration-300`}>
        {/* Page title — shown on all steps */}
        <h1 className='text-[26px] font-bold text-white text-center mb-8'>
          Activate Gift Card
        </h1>

        {state.step === 0 && (
          <CodeEntryStep initialCode={initialCode} onNext={advance} />
        )}

        {state.step === 1 && state.packageSummary && (
          <ActivationFormStep
            code={state.code}
            activationSessionToken={state.activationSessionToken}
            packageSummary={state.packageSummary}
            defaultTab={defaultTab}
            onNext={advance}
            onBack={back}
          />
        )}

        {state.step === 2 && state.packageSummary && (
          <ActivationSuccessStep
            packageSummary={state.packageSummary}
            flow={state.flow}
            accountPreview={state.accountPreview}
            onReset={reset}
          />
        )}
      </div>
    </div>
  );
}
